import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Send } from "lucide-react";
import { api, errorMessage, mediaSrc, PLATFORMS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PlatformTile } from "@/components/PlatformTile";

const LIMITS = { tiktok: 2200, reels: 2200, shorts: 100, x: 280, linkedin: 3000, facebook: 2000 };

export default function Compose() {
  const { batchId } = useParams();
  const [history, setHistory] = useState([]);
  const [batch, setBatch] = useState(null);
  const [selected, setSelected] = useState(null);
  const [posts, setPosts] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/history").then((r) => setHistory(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!batchId) return;
    api.get(`/batches/${batchId}`).then((r) => { setBatch(r.data); const first = r.data.variations.find((v) => v.approved && v.status === "rendered") || r.data.variations.find((v) => v.status === "rendered"); setSelected(first || null); }).catch((e) => toast.error(errorMessage(e)));
  }, [batchId]);
  useEffect(() => {
    if (!selected) return;
    const base = {};
    (batch?.prefs?.platforms || []).forEach((p) => { base[p] = selected.posts?.[p] ?? `${selected.hook ? selected.hook + "\n\n" : ""}${selected.caption || ""}${selected.hashtags?.length ? "\n\n" + selected.hashtags.map((h) => `#${h}`).join(" ") : ""}`; });
    setPosts(base);
  }, [selected, batch]);

  const platforms = useMemo(() => PLATFORMS.filter((p) => batch?.prefs?.platforms?.includes(p.id)), [batch]);
  const candidates = useMemo(() => (batch?.variations || []).filter((v) => v.status === "rendered"), [batch]);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/variations/${selected.id}/posts`, { posts }); toast.success("Posts saved"); setBatch((b) => ({ ...b, variations: b.variations.map((v) => (v.id === selected.id ? { ...v, posts } : v)) })); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const suggest = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/variations/${selected.id}/suggest-posts`); setPosts((p) => ({ ...p, ...data.posts })); toast.success("AI suggestions applied"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const publish = async () => {
    try { await api.post(`/variations/${selected.id}/publish`); } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!batchId) {
    return (
      <div className="space-y-6 rise" data-testid="compose-picker">
        <div><span className="overline-label">Compose</span><h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Pick a batch to write posts for</h1></div>
        {history.length === 0 && <p className="text-muted-foreground">No batches yet. <Link to="/editor" className="text-primary hover:underline">Render one in the editor</Link>.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {history.map((b) => (
            <Link key={b.id} to={`/compose/${b.id}`} className="card-surface card-hover p-5 block" data-testid={`compose-batch-${b.id}`}>
              <p className="font-semibold truncate">{b.project_title}</p>
              <p className="text-xs font-mono text-muted-foreground mt-1">{b.counts?.rendered || 0} rendered · {b.counts?.approved || 0} approved</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }
  if (!batch) return <div className="h-64 rounded-2xl shimmer" />;

  return (
    <div className="space-y-6 rise" data-testid="compose-page">
      <div><span className="overline-label">Compose · {batch.project_title}</span><h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Platform posts</h1></div>
      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 card-surface p-4 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="compose-variation-list">
          <span className="overline-label">Rendered variations ({candidates.length})</span>
          {candidates.length === 0 && <p className="text-sm text-muted-foreground py-6">Nothing rendered yet.</p>}
          {candidates.map((v) => (
            <button key={v.id} onClick={() => setSelected(v)} className={`w-full flex items-center gap-3 rounded-xl p-2 text-left transition-colors ${selected?.id === v.id ? "bg-primary/10" : "hover:bg-muted"}`} data-testid={`compose-select-${v.index}`}>
              <img src={mediaSrc(v.thumb_url)} alt="" className="h-14 w-10 rounded-lg object-cover bg-black" />
              <div className="min-w-0"><p className="text-sm font-semibold truncate">{v.hook}</p><p className="text-[10px] font-mono text-muted-foreground">#{v.index + 1} · {v.plan.framing} · {v.approved ? "approved" : "not approved"}</p></div>
            </button>
          ))}
        </aside>
        <section className="lg:col-span-8 space-y-4">
          {selected ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Button onClick={suggest} disabled={busy} variant="outline" className="btn-pill" data-testid="compose-suggest-button"><span className="ai-badge h-6 w-6 text-[10px] mr-2">AI</span> Write captions per platform</Button>
                <Button onClick={save} disabled={busy} className="btn-pill" data-testid="compose-save-button"><Save size={14} className="mr-2" /> Save posts</Button>
                <Button onClick={publish} variant="ghost" className="btn-pill ml-auto" data-testid="compose-publish-button"><Send size={14} className="mr-2" /> Publish</Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {platforms.map((p) => {
                  const text = posts[p.id] || "";
                  const over = text.length > LIMITS[p.id];
                  return (
                    <div key={p.id} className="card-surface p-4 space-y-2" data-testid={`compose-platform-${p.id}`}>
                      <div className="flex items-center justify-between"><p className="font-semibold text-sm flex items-center gap-2"><PlatformTile id={p.id} size={24} /> {p.label}</p><span className={`font-mono text-xs ${over ? "text-destructive" : "text-muted-foreground"}`} data-testid={`compose-count-${p.id}`}>{text.length}/{LIMITS[p.id]}</span></div>
                      <Textarea value={text} onChange={(e) => setPosts({ ...posts, [p.id]: e.target.value })} rows={6} className="rounded-xl text-sm" data-testid={`compose-text-${p.id}`} />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Publishing requires a connected platform account. Configure OAuth credentials under <Link to="/connections" className="text-primary hover:underline">Connections</Link>.</p>
            </>
          ) : <p className="text-muted-foreground">Select a rendered variation.</p>}
        </section>
      </div>
    </div>
  );
}
